import { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { FormProvider, useFieldArray, useForm } from 'react-hook-form';
import { IOrderWithProductPrices } from 'types/orders';
import { NudosButton } from 'components/NudosComponents';
import { CURRENCIES } from 'types/bussines';
import { FREE_ZONE_SPECIAL_BUSINESS_NAME_TYPES } from 'utils/productDefinitions';
import { IOrderPriceEditionForm } from './OrderPriceEdition.types';
import {
  buildPriceEditionPayload,
  EDITION_FORM_DEFAULT_VALUES,
  initializePriceEditionForm
} from 'utils/orderPriceEdition';
import { IProductInfo } from 'types/products';
import { saveOrderPriceEdition } from 'services/priceEdition/orderPriceEdition.service';
import CommentsModal from './components/CommentsModal/CommentsModal';
import OrderEditionHeader from './components/OrderEditionHeader/OrderEditionHeader';
import ToolEditionCard from './components/ToolEditionCard/ToolEditionCard';
import OrderEditionTotals from './components/OrdenEditionTotals/OrderEditionTotals';
import { displayErrorNotification, displaySuccessNotification } from 'utils/displayNudosStandardNotifications';

import './OrderPriceEditionModule.scss';

interface IOrderPriceEditionModuleProps {
  orderData?: IOrderWithProductPrices;
}

const OrderPriceEditionModule = ({ orderData }: IOrderPriceEditionModuleProps) => {
  const methods = useForm<IOrderPriceEditionForm>({
    defaultValues: EDITION_FORM_DEFAULT_VALUES
  });
  const { control, handleSubmit, reset } = methods;
  const { fields: sales } = useFieldArray({
    control,
    name: 'sales'
  });

  const history = useHistory();

  const [hasTaxes, setHasTaxes] = useState(false);
  const [showCommentsModal, setShowCommentsModal] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  // no puede existir el specialBusinessNameTypeId 0
  const shouldApplyTaxes =
    FREE_ZONE_SPECIAL_BUSINESS_NAME_TYPES.includes(orderData?.billingData?.specialBusinessNameTypeId || 0) &&
    orderData?.shipments?.some(shipment => shipment.destinationCountry.code === orderData?.billingData?.countryCode);

  const calculatedIvaPercentage = shouldApplyTaxes ? Number(orderData?.orderPrice?.tax ?? 0) / 100 : 0;
  /**
   * Agrupa las ventas por nombre de producto (marca/descripción)
   */
  const groupedSales = sales.reduce((groups, sale, index) => {
    const productName = sale.products?.name || 'Sin nombre';
    if (!groups[productName]) {
      groups[productName] = [];
    }
    groups[productName].push({ sale, index });
    return groups;
  }, {} as Record<string, Array<{ sale: typeof sales[0]; index: number }>>);

  /**
   * Maneja el envío del formulario de edición de precios
   */
  const onSubmit = async (data: IOrderPriceEditionForm) => {
    setIsSaving(true);
    const dataCopy: IOrderPriceEditionForm = JSON.parse(JSON.stringify(data));
    dataCopy.sales = data.sales?.map(sale => {
      return {
        ...sale,
        products: sale?.products as IProductInfo | null
      };
    });
    reset(dataCopy);
    const payload = buildPriceEditionPayload(dataCopy, orderData, shouldApplyTaxes);

    if (!payload) {
      displayErrorNotification({
        customJSXMessage: <>No se pudo crear el payload de edición</>
      });
      setIsSaving(false);
      return;
    }

    try {
      await saveOrderPriceEdition(payload);
      displaySuccessNotification({
        customJSXMessage: <>{`El resumen de la orden fue editado con éxito`}</>
      });
      history.push(`/order/details/${orderData?.id}`, { initialTab: 3 });
    } catch (error) {
      displayErrorNotification({
        customJSXMessage: (
          <>No se pudieron guardar los cambios, intenta de nuevo o comunícate con soporte para obtener ayuda.</>
        )
      });
    } finally {
      setShowCommentsModal(false);
      setIsSaving(false);
    }
  };

  /**
   * Maneja la cancelación de la edición y navega hacia atrás.
   * Si existe un ID de orden, redirige al detalle de la orden, de lo contrario vuelve a la página anterior.
   */
  const handleCancelAndGoBack = () => {
    if (orderData?.id) {
      history.push(`/order/details/${orderData.id}`);
    } else {
      history.goBack();
    }
  };

  /**
   * Ajusta los valores de venta en USD agregando IVA cuando corresponde.
   */
  const applyTaxesToSales = (sales: IOrderPriceEditionForm['sales'], taxRate: number) => {
    if (!taxRate) {
      return sales;
    }
    const multiplier = 1 + taxRate;
    return sales.map(sale => {
      const baseValueUsd = Number(sale.saleValueUsd);
      if (!Number.isFinite(baseValueUsd) || baseValueUsd <= 0) {
        return sale;
      }
      return {
        ...sale,
        saleValueUsd: Number((baseValueUsd * multiplier).toFixed(2))
      };
    });
  };

  /**
   * Inicializa el formulario con los datos de la orden, determina si aplica impuestos
   * y ajusta los montos base antes de renderizar los componentes controlados.
   */
  const initializeFormValues = () => {
    const formValues = initializePriceEditionForm(orderData);
    const salesWithTaxes = shouldApplyTaxes
      ? applyTaxesToSales(formValues.sales, calculatedIvaPercentage)
      : formValues.sales;
    reset({
      ...formValues,
      sales: salesWithTaxes
    });
    setHasTaxes(Boolean(shouldApplyTaxes));
  };

  useEffect(() => {
    initializeFormValues();
  }, [orderData]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <FormProvider {...methods}>
      <form className="editOrder" onSubmit={handleSubmit(onSubmit)}>
        {/* Nota: Cuando el modal está abierto, el botón de envío está en CommentsModal. Cuando el modal está cerrado, el botón de envío está en el formulario. */}
        {showCommentsModal && (
          <CommentsModal handleCloseModal={() => setShowCommentsModal(false)} isSaving={isSaving} />
        )}
        <main className="editionContent">
          <OrderEditionHeader
            companyName={orderData?.billingData?.name ?? ''}
            orderId={orderData?.id}
            exchangeRate={Number(orderData?.orderPrice?.exchangeRate)}
            currency={orderData?.orderPrice?.currency}
          />
          <h2 className="editOrderTitle">Resumen de la orden</h2>
          <section className="cardsContainer">
            {Object.entries(groupedSales).map(([productName, salesInGroup], groupIndex) => (
              <div className="toolCardContainer" key={productName}>
                <ToolEditionCard productName={productName} salesInGroup={salesInGroup} />
                {groupIndex + 1 < Object.keys(groupedSales).length && <hr />}
              </div>
            ))}
          </section>
          <hr className="divider" />
          <OrderEditionTotals
            taxRate={calculatedIvaPercentage}
            currency={orderData?.orderPrice?.currency || CURRENCIES.USD}
            hasTaxes={hasTaxes}
            hasCommission={orderData?.paymentType?.name?.toLowerCase().includes('tarjeta') || false}
          />
        </main>
        <footer className="formFooter">
          <NudosButton
            customClassName="orderPriceEditionButton"
            buttonText="Cancelar y volver"
            colorPalette="secondary"
            handleClick={handleCancelAndGoBack}
          />
          <NudosButton
            customClassName="orderPriceEditionButton"
            buttonText="Guardar cambios"
            colorPalette="primary"
            type={'button'}
            handleClick={() => setShowCommentsModal(true)}
            isButtonLoading={isSaving}
          />
        </footer>
      </form>
    </FormProvider>
  );
};
export default OrderPriceEditionModule;
